import { useState, type ReactNode } from "react";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { CadenceCounts } from "@/types/contentCalendar.type";
import { KitButton } from "../../components/kit/Button";
import { KitModal } from "../../components/kit/Modal";
import { KitStepper } from "../../components/kit/Switch";

/**
 * Posts a month, edited where it is read (Figma 08, 1 First run): the three
 * format counts, each with its stepper, then their sum. The same three numbers
 * exist at two scopes and this edits either:
 *
 * - "brand": the year page's Posts a month. The brand's numbers for every month
 *   Creo plans from now on, compared with what the strategy asks for, with
 *   All calendar settings at the foot.
 * - "month": a month's own scope ("December scope"). Each format says how many
 *   are planned against it (over in red, short in grey), the month's total
 *   counts its quick posts, and Re-plan sits at the foot.
 *
 * There is no separate total: the total IS their sum, so the two can never
 * disagree. The limits are the server's: 0-30 a format, 1-30 in all.
 */

const MIN_TOTAL = 1;
const MAX_TOTAL = 30;
const MAX_PER_BUCKET = 30;

export type CadenceDraft = CadenceCounts;

type FormatKey = keyof CadenceCounts;
const FORMATS: Array<{ key: FormatKey; label: string; noun: [string, string] }> = [
  { key: "staticPostsPerMonth", label: "Static", noun: ["static", "statics"] },
  { key: "verticalVideoPostsPerMonth", label: "Vertical video", noun: ["vertical video", "vertical videos"] },
  { key: "horizontalVideoPostsPerMonth", label: "Horizontal video", noun: ["horizontal video", "horizontal videos"] },
];

/** What a month already holds against its scope, per format, and its quick posts. */
export interface PlannedCounts {
  staticPostsPerMonth: number;
  verticalVideoPostsPerMonth: number;
  horizontalVideoPostsPerMonth: number;
  /** Posts added by hand (quick posts): they count toward the month. */
  quick: number;
}

function sumOf(counts: CadenceCounts): number {
  return counts.staticPostsPerMonth + counts.verticalVideoPostsPerMonth + counts.horizontalVideoPostsPerMonth;
}

function plannedLine(planned: number, scope: number): ReactNode {
  const gap = planned - scope;
  return (
    <>
      {planned} planned
      {gap > 0 ? (
        <>
          {" · "}
          <span className="text-co-danger">{gap} over</span>
        </>
      ) : gap < 0 ? (
        ` · ${-gap} short`
      ) : null}
    </>
  );
}

/** "The strategy asks for 5 vertical videos. This is 1 more." for each format that differs. */
function strategyLines(draft: CadenceCounts, strategy: CadenceCounts): string[] {
  return FORMATS.flatMap(({ key, noun }) => {
    const asked = strategy[key];
    const gap = draft[key] - asked;
    if (gap === 0) return [];
    return [`The strategy asks for ${asked} ${asked === 1 ? noun[0] : noun[1]}. This is ${Math.abs(gap)} ${gap > 0 ? "more" : "fewer"}.`];
  });
}

const FOOT_LINK =
  "inline-flex items-center gap-2 text-[13.5px] text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:opacity-50";

export default function CadenceDialog({
  scope,
  title,
  description,
  initial,
  saving,
  onSave,
  onClose,
  strategy = null,
  onUseStrategy,
  reach,
  onOpenSettings,
  monthName,
  planned,
  quickLine,
  brandLine,
  onReset,
  onReplan,
}: {
  scope: "brand" | "month";
  title: string;
  description: string;
  /** What the steppers open on: the brand's numbers, or the month's own. */
  initial: CadenceCounts;
  saving?: boolean;
  onSave: (draft: CadenceDraft) => Promise<void> | void;
  onClose: () => void;
  /** Brand: the active strategy's numbers, to say how these compare. */
  strategy?: CadenceCounts | null;
  onUseStrategy?: () => void;
  /** Brand: which month starts from these numbers next, and which planned months keep theirs. */
  reach?: string | null;
  onOpenSettings?: () => void;
  /** Month: the month's name, for the total row and the save button. */
  monthName?: string;
  planned?: PlannedCounts;
  /** Month: "The quick post on Fri 11 Dec makes December 6 of 5." when a quick post tips it. */
  quickLine?: string | null;
  /** Month: "Other months start from Fatafeat's 12 a month, in the calendar settings." */
  brandLine?: string;
  /** Month: given when the month has numbers of its own; puts the brand's back. */
  onReset?: () => Promise<void> | void;
  /**
   * Month, planned: throw its posts away and plan it again. Handed the numbers
   * on screen when they differ from `initial`, so a re-plan runs on what was
   * typed; null when unchanged.
   */
  onReplan?: (changed: CadenceDraft | null) => void;
}) {
  const [draft, setDraft] = useState<CadenceDraft>({
    staticPostsPerMonth: initial.staticPostsPerMonth,
    verticalVideoPostsPerMonth: initial.verticalVideoPostsPerMonth,
    horizontalVideoPostsPerMonth: initial.horizontalVideoPostsPerMonth,
  });
  const month = scope === "month";
  const total = sumOf(draft);
  const problem =
    total < MIN_TOTAL
      ? "A month needs at least one post. Give at least one format a count."
      : total > MAX_TOTAL
        ? `That is ${total} posts a month; the most across all formats is ${MAX_TOTAL}.`
        : null;
  const changed = FORMATS.some(({ key }) => draft[key] !== initial[key]);
  const set = (key: FormatKey, value: number) => setDraft((previous) => ({ ...previous, [key]: value }));

  const plannedTotal = planned ? sumOf(planned) + planned.quick : null;
  const strategyNotes = !month && strategy ? strategyLines(draft, strategy) : [];
  const sameAsStrategy = !month && strategy && strategyNotes.length === 0;

  const notes: ReactNode[] = month
    ? [
        "A month’s posts are its planned and quick posts.",
        ...(quickLine ? [quickLine] : []),
        ...(brandLine
          ? [
              <span key="brand">
                {brandLine}
                {onReset ? (
                  <>
                    {" "}
                    <button type="button" disabled={saving} onClick={() => void onReset()} className="text-co-accent-ink outline-none hover:underline focus-visible:underline">
                      Use them here
                    </button>
                  </>
                ) : null}
              </span>,
            ]
          : []),
      ]
    : [
        ...(sameAsStrategy && strategy
          ? [
              `Same as the strategy: ${FORMATS.map(({ key, noun }) => `${strategy[key]} ${noun[0]}`).join(", ")}.`,
            ]
          : strategyNotes),
        ...(strategyNotes.length && onUseStrategy
          ? [
              <button
                key="use"
                type="button"
                disabled={saving}
                onClick={onUseStrategy}
                className="w-fit text-co-accent-ink outline-none hover:underline focus-visible:underline disabled:opacity-50"
              >
                Use the strategy’s numbers
              </button>,
            ]
          : []),
        ...(reach ? [reach] : []),
      ];

  return (
    <KitModal
      isOpen
      onOpenChange={(open) => !open && !saving && onClose()}
      title={title}
      description={description}
      modalClassName={month ? "max-w-[560px]" : "max-w-[520px]"}
    >
      <div className="flex flex-col">
        {month ? (
          <div className="flex items-center justify-between pb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted">
            <span>Format</span>
            <span className="w-[106px] text-center">Scope</span>
          </div>
        ) : null}
        {FORMATS.map(({ key, label }) => (
          <div key={key} className="flex min-h-[46px] items-center gap-4">
            <span className="min-w-0 flex-1 text-[15px] text-co-text">{label}</span>
            {planned ? <span className="text-[13px] text-co-text2">{plannedLine(planned[key], draft[key])}</span> : null}
            <KitStepper value={draft[key]} label={label} min={0} max={MAX_PER_BUCKET} onChange={(n) => set(key, n)} disabled={saving} />
          </div>
        ))}
        <div className="mt-1 flex min-h-[46px] items-center gap-4 border-t border-co-line">
          <span className="min-w-0 flex-1 text-[15px] font-semibold text-co-text">{month ? monthName : "Every month"}</span>
          {plannedTotal !== null ? <span className="text-[13px] text-co-text2">{plannedLine(plannedTotal, total)}</span> : null}
          <span className={cn("w-[106px] text-right text-[15px] font-semibold tabular-nums", problem ? "text-co-danger" : "text-co-text", month && "text-center")}>
            {total} {total === 1 ? "post" : "posts"}
          </span>
        </div>
        {problem ? (
          <p role="alert" className="mt-1 text-[13px] text-co-alert-ink">
            {problem}
          </p>
        ) : null}
        {notes.length ? (
          <div className="mt-4 flex flex-col gap-2 rounded-card border border-co-line2 px-4 py-3.5 text-[13.5px] leading-[1.45] text-co-text2">
            {notes.map((note, index) => (
              <div key={index}>{note}</div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        {month && onReplan ? (
          <button type="button" disabled={!!problem || saving} onClick={() => onReplan(changed ? draft : null)} className={FOOT_LINK}>
            <RotateCcw className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            Re-plan {monthName}
          </button>
        ) : null}
        {!month && onOpenSettings ? (
          <button type="button" disabled={saving} onClick={onOpenSettings} className={FOOT_LINK}>
            <ArrowUpRight className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            All calendar settings
          </button>
        ) : null}
        <span className="ml-auto flex items-center gap-3">
          <KitButton variant="ghost" onClick={onClose} isDisabled={saving}>
            Cancel
          </KitButton>
          <KitButton onClick={() => void onSave(draft)} isDisabled={!!problem || saving || (!changed && !month)}>
            {month ? `Save for ${monthName}` : "Save"}
          </KitButton>
        </span>
      </div>
    </KitModal>
  );
}
